'use client'

import {grid, gridItem} from '~/design-system/foundations'
import HgArticleHeroText, {
  type HgArticleHeroTextProps,
} from '~/design-system/hg/components/HgArticleHeroText'
import {cn} from '~/design-system/utils'
import {tv} from 'tailwind-variants'
import {useEffect, useRef} from 'react'
import {useIsVisible} from '~/design-system/hooks/useIsVisible'

export const ARTICLE_HERO_BUTTON_CONTAINER_ID = 'animation-button-container'

export type HgArticleHeroHighHorizontalProps = {
  assetType: 'horizontal'
  /** LG breakpoint asset */
  assetSixteenNineRatio: React.ReactNode
  /** MD breakpoint asset */
  assetOneOneRatio?: React.ReactNode
  /** XS breakpoint asset */
  assetNineSixteenRatio?: React.ReactNode
  className?: string
} & HgArticleHeroTextProps

const hgArticleHeroHighHorizontalVariants = tv({
  slots: {
    sixteenNineRatio: 'aspect-h-9 aspect-w-16 h-screen w-full [&_img]:object-cover',
    oneOneRatio: 'aspect-h-1 aspect-w-1 h-screen w-full [&_img]:object-cover',
    nineSixteenRatio: 'aspect-h-16 aspect-w-9 h-screen w-full [&_img]:object-cover',
  },
  variants: {
    hasOneOneRatio: {
      true: {
        sixteenNineRatio: 'hidden lg:block',
        oneOneRatio: 'lg:hidden',
      },
    },
    hasNineSixteenRatio: {
      true: {
        sixteenNineRatio: 'hidden md:block',
        nineSixteenRatio: 'md:hidden',
      },
    },
    hasBothAssets: {
      true: {
        sixteenNineRatio: 'hidden lg:block',
        oneOneRatio: 'hidden md:block lg:hidden',
        nineSixteenRatio: 'md:hidden',
      },
    },
  },
})

export default function HgArticleHeroHighHorizontal({
  assetSixteenNineRatio,
  assetOneOneRatio,
  assetNineSixteenRatio,
  className,
  ...articleHeroTextProps
}: HgArticleHeroHighHorizontalProps) {
  const {sixteenNineRatio, oneOneRatio, nineSixteenRatio} =
    hgArticleHeroHighHorizontalVariants({
      hasOneOneRatio: !!assetOneOneRatio,
      hasNineSixteenRatio: !!assetNineSixteenRatio,
      hasBothAssets: !!assetOneOneRatio && !!assetNineSixteenRatio,
    })

  const containerRef = useRef<HTMLDivElement | null>(null)
  const opacityContainerRef = useRef<HTMLDivElement | null>(null)
  const textContainerRef = useRef<HTMLDivElement | null>(null)
  const navGradientRef = useRef<HTMLDivElement | null>(null)
  const assetRef = useRef<HTMLDivElement | null>(null)

  const isContainerVisible = useIsVisible(containerRef, {threshold: 0})
  const isOpacityContainerVisible = useIsVisible(opacityContainerRef, {
    threshold: 0.1,
  })

  useEffect(() => {
    if (!opacityContainerRef.current || !textContainerRef.current) return

    const observer = new IntersectionObserver(adjustOpacity, {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    })

    function adjustOpacity(entries: IntersectionObserverEntry[]) {
      if (!opacityContainerRef.current) return
      const entry = entries[0]
      opacityContainerRef.current.style.setProperty(
        '--opacity',
        entry.intersectionRatio.toString()
      )
    }
    observer.observe(textContainerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!navGradientRef.current) return
    navGradientRef.current.setAttribute(
      'aria-hidden',
      String(!isOpacityContainerVisible)
    )
  }, [isOpacityContainerVisible])

  /* Because the asset is fixed, the intersection observer that pauses the video when the element is not visible will not work
   * Intersection Observer doesn't check to see if there are elements on top of the target element.
   * This means the video will continue to play even when the asset is not "visible" (i.e. when scrolled past the hero) unless we manually hide it. */
  useEffect(() => {
    if (!assetRef.current) return
    assetRef.current.setAttribute('aria-hidden', String(!isContainerVisible))
  }, [isContainerVisible])

  return (
    <div
      className="relative z-0 grid h-[200vh] w-full bg-background-default pt-blog-navbars-height"
      ref={containerRef}
      data-nav-hidden
    >
      <div
        ref={navGradientRef}
        className="to-transparent pointer-events-none fixed col-start-1 row-start-1 -mt-blog-navbars-height h-navbar-height w-full bg-gradient-to-b from-[rgba(0,0,0,0.56)] transition-opacity aria-hidden:opacity-0"
      ></div>
      <div
        style={{'--opacity': 0.56} as React.CSSProperties}
        ref={opacityContainerRef}
        className="pointer-events-none sticky col-start-1 row-start-1 -mt-blog-navbars-height bg-[rgba(0,0,0,0.56)] opacity-[var(--opacity)] transition-opacity duration-100 ease-linear"
      ></div>
      <div
        id={ARTICLE_HERO_BUTTON_CONTAINER_ID}
        className="sticky top-blog-navbars-height z-[1] col-start-1 row-start-1 grid h-[calc(100vh-var(--blog-navbars-height))]"
      />
      <div
        ref={textContainerRef}
        className={grid({
          className: cn(
            className,
            'relative col-start-1 row-start-1 mx-auto h-blog-screen-without-navbars place-items-center pt-s9'
          ),
        })}
      >
        <div
          className={gridItem({
            size: 'max',
            className: 'pb-s7 sm:col-span-8 sm:col-start-3',
          })}
        >
          <HgArticleHeroText
            classNames={{
              container: 'lg:grid lg:grid-cols-12 lg:gap-x-16',
              tag: 'lg:col-span-full',
              heading: 'lg:col-span-full',
              subheading: 'lg:col-span-8 lg:col-start-3',
            }}
            {...articleHeroTextProps}
          />
        </div>
      </div>
      <div
        className="fixed inset-0 -z-[1] col-start-1 row-start-1 h-screen overflow-hidden aria-hidden:hidden"
        ref={assetRef}
      >
        <div className={sixteenNineRatio()}>{assetSixteenNineRatio}</div>
        {assetOneOneRatio && <div className={oneOneRatio()}>{assetOneOneRatio}</div>}
        {assetNineSixteenRatio && (
          <div className={nineSixteenRatio()}>{assetNineSixteenRatio}</div>
        )}
      </div>
    </div>
  )
}
